/* mwf-3d.js — le visualiseur en trois dimensions du configurateur.

   ATTENTION : fichier généré. Ne pas le modifier ici — il est refabriqué par
   MWF_Visualizer/tools/batir-asset-3d.js, qui va chercher les shaders dans
   tools/lib/noyau-3d.js. Toute retouche directe serait perdue au prochain
   passage, et ferait diverger la boutique du banc d'essai.

   Ce que ce fichier apporte, et pourquoi il est séparé du configurateur : rien
   n'y est indispensable. S'il ne se charge pas, si WebGL 2 manque, si les
   maillages n'arrivent pas, le configurateur continue d'afficher ses huit
   tuiles comme avant. Le repli n'est pas un mode dégradé écrit pour
   l'occasion : c'est le composant d'origine, intact.

   Trois choses que la troisième dimension apporte, et qu'aucune tuile ne
   pouvait donner : la pièce tourne librement au lieu de sauter d'un huitième de
   tour ; le grain d'impression suit les couches dans le repère de l'objet, donc
   il reste collé à la matière ; et la gravure devient une texture posée sur le
   panneau, donc juste sous tous les angles au lieu d'être projetée. */
(function () {
  "use strict";

  /* ---- décodage du paquet MWF3 ------------------------------------------- */
  function lirePaquet(buf) {
    const dv = new DataView(buf);
    if (String.fromCharCode(dv.getUint8(0), dv.getUint8(1), dv.getUint8(2), dv.getUint8(3)) !== "MWF3")
      throw new Error("paquet illisible");
    const bits = dv.getUint16(6, true);
    const nv = dv.getUint32(8, true), nf = dv.getUint32(12, true);
    const lo = [dv.getFloat32(16, true), dv.getFloat32(20, true), dv.getFloat32(24, true)];
    const ech = [dv.getFloat32(28, true), dv.getFloat32(32, true), dv.getFloat32(36, true)];
    let o = 40;
    const pos = new Uint16Array(buf, o, nv*3); o += nv*6;
    const nrm = new Int8Array(buf, o, nv*3);   o += nv*3;
    const grain = new Uint8Array(buf, o, nv);  o += nv;
    const idx = bits === 16 ? new Uint16Array(buf, o, nf*3) : new Uint32Array(buf, o, nf*3);
    return { nv, nf, lo, ech, pos, nrm, grain, idx };
  }

  /* ---- algèbre : juste ce qu'il faut, en colonnes comme WebGL ------------- */
  function perspective(fov, aspect, near, far) {
    const f = 1 / Math.tan(fov/2);
    return [f/aspect,0,0,0, 0,f,0,0, 0,0,(far+near)/(near-far),-1, 0,0,2*far*near/(near-far),0];
  }
  /* Produit en convention colonne : m[c*4+r] est la ligne r de la colonne c. */
  function mul(a, b) {
    const o = new Array(16).fill(0);
    for (let c = 0; c < 4; c++) for (let r = 0; r < 4; r++) {
      let s = 0;
      for (let k = 0; k < 4; k++) s += a[k*4+r] * b[c*4+k];
      o[c*4+r] = s;
    }
    return o;
  }
  /* Vue en tourne-disque, axe vertical Z — celui de l'impression, donc celui des
     maillages. La caméra orbite, la cible reste au centre de la pièce, et
     l'élévation est bornée pour que la verticale ne dégénère pas au zénith. */
  function vue(az, el, dist, cible) {
    const e = Math.max(-1.53, Math.min(1.53, el));
    const ce = Math.cos(e), se = Math.sin(e), ca = Math.cos(az), sa = Math.sin(az);
    const oeil = [cible[0] + dist*ce*ca, cible[1] + dist*ce*sa, cible[2] + dist*se];
    const f = [cible[0]-oeil[0], cible[1]-oeil[1], cible[2]-oeil[2]];
    const ln = Math.hypot(f[0],f[1],f[2]) || 1; f[0]/=ln; f[1]/=ln; f[2]/=ln;
    const s = [f[1], -f[0], 0];
    const ls = Math.hypot(s[0],s[1],s[2]) || 1; s[0]/=ls; s[1]/=ls; s[2]/=ls;
    const u = [s[1]*f[2]-s[2]*f[1], s[2]*f[0]-s[0]*f[2], s[0]*f[1]-s[1]*f[0]];
    return [s[0],u[0],-f[0],0, s[1],u[1],-f[1],0, s[2],u[2],-f[2],0,
            -(s[0]*oeil[0]+s[1]*oeil[1]+s[2]*oeil[2]),
            -(u[0]*oeil[0]+u[1]*oeil[1]+u[2]*oeil[2]),
              f[0]*oeil[0]+f[1]*oeil[1]+f[2]*oeil[2], 1];
  }
  /* Matrice normale : la vue n'est qu'une rotation suivie d'une translation, sa
     partie linéaire est orthogonale — on peut donc l'utiliser telle quelle sans
     passer par une inverse transposée. */
  function normaleDe(v) {
    return [v[0],v[1],v[2], v[4],v[5],v[6], v[8],v[9],v[10]];
  }

  const VS = `#version 300 es
  in vec3 aPos; in vec3 aNrm; in float aGrain;
  uniform mat4 uMVP; uniform vec3 uLo; uniform vec3 uEch; uniform float uDz;
  out vec3 vN; out vec3 vObj; out float vGrain;
  void main() {
    vec3 p = uLo + aPos * uEch;
    p.z += uDz;
    vObj = p;
    /* La normale part en repère objet, pas en repère caméra : le grain se
       calcule là, puisqu'il est gravé dans la matière et doit rester collé à
       elle quand la pièce tourne. La conversion vers la caméra attend donc que
       la perturbation soit faite. */
    vN = aNrm;
    vGrain = aGrain;
    gl_Position = uMVP * vec4(p, 1.0);
  }`;

  /* L'éclairage reprend celui des tuiles : une clé haute et de face, un
     remplissage doux venu de la droite, fixés dans le repère caméra. Un studio
     dont le rig ne tourne pas avec l'objet.

     Le grain, lui, reprend le modèle validé à l'oeil sur la boutique, porté du
     plan à la surface. Trois traits en font la vraisemblance, et aucun n'est
     décoratif :

     - les touffes s'alignent sur les couches d'impression, donc sur z ;
     - chaque couche est décalée au hasard, sans quoi elles forment des colonnes
       et la paroi prend un air de tissu tissé ;
     - les creux sont amortis et les crêtes conservées : une surface rugueuse
       s'auto-ombre, et un grain symétrique se lit comme de la saleté.

     La différence avec la version en tuiles est que le relief est ici un vrai
     relief — on perturbe la normale, la lumière fait le reste — au lieu d'une
     modulation de luminance calculée pour un angle donné. */
  const FS = `#version 300 es
  precision highp float;
  in vec3 vN; in vec3 vObj; in float vGrain;
  uniform mat3 uNrmMat;
  uniform vec3 uTeinte; uniform float uMontrerZones;
  uniform vec2 uStade;        // rayon et demi-longueur du contour, en mm
  uniform vec4 uGrainReg;     // cellule d'arc, cellule verticale, amplitude, amorti des creux
  uniform vec2 uNus;          // cellules entières sur le tour, grosse et fine
  uniform float uGrainOn;
  uniform sampler2D uTexte;
  uniform vec4 uPanneau;      // largeur, hauteur, altitude du dessus, actif
  uniform vec3 uLettre; uniform float uProfondeur; uniform float uCreuse;
  uniform float uBiseau;     // largeur du biseau, en texels
  out vec4 sortie;

  const float PI = 3.141592653589793;
  const vec3 CLE  = normalize(vec3(-0.28, 0.60, 0.75));
  const vec3 REMP = normalize(vec3( 0.72, 0.05, 0.69));

  /* L'éclairage se décompose, parce qu'une ombre n'assombrit pas : elle bloque
     une source. Le second paramètre est la fraction de lumière principale qui
     atteint le point — un dans la pleine lumière, zéro à l'ombre d'une paroi. En
     perdre la totalité coûte 0,400 sur un peu plus de 1,0 : c'est franc, et ça
     n'a pas été réglé au jugé. Un coefficient d'assombrissement arbitraire, lui,
     se lit toujours comme une autre couleur. */
  float eclairerAvec(vec3 n, float cle) {
    return 0.500 + 0.400 * cle * max(0.0, dot(n, CLE)) + 0.130 * max(0.0, dot(n, REMP));
  }
  float eclairer(vec3 n) { return eclairerAvec(n, 1.0); }

  float hash21(vec2 p) {
    vec3 q = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973));
    q += dot(q, q.yzx + 33.33);
    return fract((q.x + q.y) * q.z);
  }

  /* Bruit de valeur, bilinéaire et adouci, périodique sur le tour de la pièce :
     sans cette périodicité, une couture apparaîtrait là où le contour se referme. */
  float bruit(vec2 c, float nu) {
    vec2 i = floor(c), f = fract(c);
    f = f * f * (3.0 - 2.0 * f);
    float x0 = mod(i.x, nu), x1 = mod(i.x + 1.0, nu);
    float h00 = hash21(vec2(x0, i.y)), h10 = hash21(vec2(x1, i.y));
    float h01 = hash21(vec2(x0, i.y + 1.0)), h11 = hash21(vec2(x1, i.y + 1.0));
    return mix(mix(h00, h10, f.x), mix(h01, h11, f.x), f.y);
  }

  float perimetre() { return 4.0 * uStade.y + 2.0 * PI * uStade.x; }

  float champ(float s, float z) {
    float per = perimetre();
    float cw = per / uNus.x, cw2 = per / uNus.y;
    float ch = uGrainReg.y;
    float rangee = floor(z / ch);
    float dec = hash21(vec2(rangee, 91.7)) * cw;
    float gros = bruit(vec2((s + dec) / cw, z / ch), uNus.x);
    float fin  = bruit(vec2((s + dec) / cw2, z / (ch / 2.3)), uNus.y);
    return mix(gros, fin, 0.4);
  }

  /* Distance parcourue le long du contour, un stade de rayon R autour du segment
     [-L, L]. Quatre secteurs, raccordés de façon continue et refermés sur
     eux-mêmes : c'est ce qui permet au grain de suivre la paroi sans que le
     maillage porte de coordonnées de texture. */
  float arcStade(vec2 p, float R, float L) {
    if (abs(p.x) <= L) return p.y < 0.0 ? (p.x + L) : (2.0*L + PI*R + (L - p.x));
    if (p.x > L)       return 2.0*L + R * (atan(p.y, p.x - L) + PI*0.5);
    return 4.0*L + PI*R + R * (atan(-p.y, -(p.x + L)) + PI*0.5);
  }

  vec3 perturber(vec3 n, vec3 p) {
    vec3 t = cross(vec3(0.0, 0.0, 1.0), n);
    float lt = length(t);
    if (lt < 1e-4) return n;               // face horizontale : pas de tangente d'arc
    t /= lt;
    vec3 b = cross(n, t);
    float s = arcStade(p.xy, uStade.x, uStade.y);
    float e = (perimetre() / uNus.x) * 0.18;
    float hs = champ(s + e, p.z) - champ(s - e, p.z);
    float hz = champ(s, p.z + e) - champ(s, p.z - e);
    return normalize(n - uGrainReg.z * (hs * t + hz * b) / e);
  }

  void main() {
    /* Une coque ouverte se regarde parfois par l'envers : on retourne alors la
       normale, faute de quoi la face s'éteindrait et se lirait comme un trou. */
    vec3 n = normalize(vN);
    if (!gl_FrontFacing) n = -n;
    vec3 p = vObj;

    /* La frontière est une ligne de niveau du degré de grain, pas une limite de
       triangle : on tranche à la moitié et elle traverse la face. */
    bool grain = vGrain > 0.5;
    float ton = eclairer(normalize(uNrmMat * n));
    if (grain && uGrainOn > 0.5) {
      float apres = eclairer(normalize(uNrmMat * perturber(n, p)));
      float d = apres - ton;
      if (d < 0.0) d *= uGrainReg.w;       // les creux s'auto-ombrent moins qu'ils ne devraient
      ton += d;
    }

    vec3 c = uTeinte * ton;

    /* La gravure occupe un panneau sur le dessus du couvercle. Pas besoin de
       coordonnées de texture dans le maillage : le panneau est plan et
       horizontal, donc les siennes se déduisent de la position. La face doit
       être rigoureusement celle du dessus — au seuil près, mesuré à |nz| = 1 —
       sinon un décrochement voisin attrape un morceau de lettre et le montre au
       mauvais endroit.

       Deux façons de graver, selon la pièce réelle : quand la couleur du texte
       diffère de celle du couvercle, c'est un changement de matière, donc à
       plat ; quand elle est la même, le texte est creusé, et seule son ombre le
       révèle. Le rendu suit cette règle au lieu d'en inventer une. */
    /* Échantillonnage de la gravure, hors de tout branchement.

       Appeler texture() dans un branchement non uniforme laisse les dérivées
       implicites indéfinies : c'est elles qui choisissent le niveau de mipmap,
       et le résultat était un liseré pointillé tout autour du panneau. On calcule
       donc les coordonnées et on échantillonne sans condition — elles varient
       continûment sur la face — puis on ne se sert du résultat que là où il
       a un sens. (Aucun accent grave dans ce fichier : il vit dans un gabarit
       de chaîne, et le premier qu'on y glisse le referme.) */
    vec2 uv = vec2(p.x / uPanneau.x + 0.5, 0.5 - p.y / uPanneau.y);
    vec2 uvClamp = clamp(uv, vec2(0.0), vec2(1.0));
    vec2 eTex = vec2(1.0) / vec2(textureSize(uTexte, 0));
    /* Le gradient se prend sur quatre texels, pas un.

       Sur un seul, le relief n'existait que sur huit centièmes de millimètre au
       bord de la lettre : invisible, et il ne restait de la gravure qu'un
       assombrissement uniforme — un simple changement de teinte, pas un creux.
       Quatre texels font 0,16 mm de chaque côté, ce qui donne au bord une
       dépouille d'environ cinquante degrés pour 0,4 mm de profondeur. Une
       gravure imprimée n'a de toute façon pas de paroi verticale. */
    float PAS = max(1.0, uBiseau);
    float a  = texture(uTexte, uvClamp).a;
    float ax = texture(uTexte, uvClamp + vec2(PAS * eTex.x, 0.0)).a
             - texture(uTexte, uvClamp - vec2(PAS * eTex.x, 0.0)).a;
    float ay = texture(uTexte, uvClamp + vec2(0.0, PAS * eTex.y)).a
             - texture(uTexte, uvClamp - vec2(0.0, PAS * eTex.y)).a;

    /* La gravure occupe un panneau sur le dessus du couvercle. Pas besoin de
       coordonnées de texture dans le maillage : le panneau est plan et
       horizontal, donc les siennes se déduisent de la position. La face doit
       être rigoureusement celle du dessus — au seuil près, mesuré à |nz| = 1 —
       sinon un décrochement voisin attrape un morceau de lettre et le montre au
       mauvais endroit.

       Deux façons de graver, selon la pièce réelle : quand la couleur du texte
       diffère de celle du couvercle, c'est un changement de matière, donc à
       plat ; quand elle est la même, le texte est creusé, et seule son ombre le
       révèle. Le rendu suit cette règle au lieu d'en inventer une. */
    bool dansPanneau = uv.x > 0.0 && uv.x < 1.0 && uv.y > 0.0 && uv.y < 1.0;
    if (uPanneau.w > 0.5 && dansPanneau && a > 0.002
        && n.z > 0.99 && abs(p.z - uPanneau.z) < 0.3) {
      /* Le relief se calcule à partir de la profondeur réelle, en millimètres,
         et non d'un coefficient réglé au jugé : la différence d'alpha porte sur
         deux texels, dont on connaît la taille sur la pièce. Un bord franc de
         0,4 mm de profondeur donne ainsi une paroi à 78°, ce qu'elle est. */
      float pasMM = 2.0 * PAS * eTex.x * uPanneau.x;
      float k = uProfondeur / max(pasMM, 1e-6);
      vec3 nt = normalize(n - k * (ax * vec3(1.0, 0.0, 0.0) + ay * vec3(0.0, -1.0, 0.0)));
      float tt = eclairer(normalize(uNrmMat * nt));
      if (uCreuse > 0.5) {
        /* Creusé. Ce qui fait voir un creux, ce n'est pas qu'il soit plus sombre
           — ce serait une autre couleur — c'est son ombre portée : la paroi du
           côté de la lumière projette une ombre sur le fond, et cette ombre se
           déplace quand la pièce tourne. On la calcule vraiment.

           La lumière est fixée dans le repère caméra ; on la ramène donc dans
           celui de l'objet (la matrice est orthogonale, sa transposée suffit),
           on la projette sur le plan du panneau, et on regarde s'il y a de la
           matière en remontant vers elle. Si oui, le point est à l'ombre de la
           paroi. Le décalage vaut la profondeur divisée par la pente du rayon,
           ce qui est exactement la longueur d'une ombre. */
        vec3 lobj = transpose(uNrmMat) * CLE;
        float monte = max(lobj.z, 0.08);
        float longueurMM = uProfondeur * length(lobj.xy) / monte;
        vec2 dir = normalize(lobj.xy + vec2(1e-5, 1e-5));
        vec2 vers = vec2(dir.x * longueurMM / uPanneau.x, -dir.y * longueurMM / uPanneau.y);
        float aOmbre = texture(uTexte, clamp(uvClamp + vers, vec2(0.0), vec2(1.0))).a;
        float dedans = smoothstep(0.35, 0.65, a);
        float bloque = 1.0 - smoothstep(0.35, 0.65, aOmbre);
        float ombre = dedans * bloque;
        /* Trois effets, tous tirés de la seule profondeur : la paroi biseautée
           accroche la lumière d'un côté, l'ombre la coupe de l'autre, et le fond
           voit un peu moins le ciel.

           Ce dernier terme reste volontairement faible. C'est le seul des trois
           qui soit uniforme sur toute la lettre, donc le seul que l'oeil lise
           comme une autre couleur plutôt que comme un creux — retour du
           propriétaire, et il a raison : ce qui creuse, c'est ce qui change avec
           l'angle. */
        vec3 nc = normalize(uNrmMat * nt);
        float occl = 1.0 - min(0.14, 0.18 * uProfondeur) * dedans;
        c = uTeinte * eclairerAvec(nc, 1.0 - ombre) * occl;
      } else {
        c = mix(c, uLettre * ton, a);
      }
    }

    if (uMontrerZones > 0.5 && grain) c = mix(c, vec3(0.85, 0.95, 0.35), 0.45);
    sortie = vec4(clamp(c, 0.0, 1.0), 1.0);
  }`;

  /* ---- la scène ---------------------------------------------------------- */

  const TOURNE = 0.008;      // radians par pixel glissé
  const ELEV_MIN = -1.35, ELEV_MAX = 1.35;
  const FOV = 21 * Math.PI / 180;
  /* Le cadre contient la sphère englobante, à six pour cent près — la marge des
     tuiles.

     Ce facteur est mesuré et non choisi : à recul égal à un, la vue la plus
     encombrante des deux formats — le 120 vu de trois quarts haut — occupe déjà
     94,4 % du demi-cadre, sur un balayage de 360 azimuts par 37 élévations et
     sur les sommets réels, pas sur la boîte englobante. Il ne reste donc presque
     rien à ajouter pour être sûr. Le banc d'essai utilisait 1,12, soit onze pour
     cent d'air en trop qui rapetissaient l'étui d'autant. */
  const SPHERE = 1.005;
  /* Bornes du zoom manuel, et pas d'un cran de molette. Celles du banc. */
  const RECUL_MIN = 0.42, RECUL_MAX = 1.8, PAS_ZOOM = 0.09;
  /* Réglages du grain et de la gravure, validés à l'oeil avec le propriétaire.
     La touffe fait la taille réelle du fuzzy skin, à un dixième près : en trois
     dimensions l'aperçu est assez fin pour s'en passer d'exagération, alors que
     les tuiles demandaient deux fois et demie la taille pour qu'elle se voie. */
  const GRAIN_TAILLE = 0.6, GRAIN_RELIEF = 0.28, GRAIN_CREUX = 0.6;
  const GRAVURE_PROFONDEUR = 0.6, GRAVURE_BISEAU = 5;
  const PANNEAU_PX = 2048;

  function compiler(gl, src, type) {
    const s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      const log = gl.getShaderInfoLog(s);
      gl.deleteShader(s);
      throw new Error("shader : " + log);
    }
    return s;
  }

  class Scene3D {
    constructor(canvas, opts) {
      this.canvas = canvas;
      this.opts = opts || {};
      /* Canvas transparent, comme celui des tuiles : le fond du thème doit
         apparaître derrière la pièce, et il change avec le mode sombre. */
      this.gl = canvas.getContext("webgl2",
        { antialias: true, alpha: true, premultipliedAlpha: true });
      if (!this.gl) throw new Error("WebGL 2 absent");
      this.pieces = {};
      this.azimut = -Math.PI / 4;      // le trois-quarts, comme la tuile de départ
      this.elevation = 21 * Math.PI / 180;
      this.recul = 1;
      this.etat = { format: "135", corps: "#1C1C1C", couvercle: "#1C1C1C",
                    lettres: "#F2F0EA", texte: "", police: 0 };
      this._prepare();
      this._brancher();
    }

    _prepare() {
      const gl = this.gl;
      const p = gl.createProgram();
      gl.attachShader(p, compiler(gl, VS, gl.VERTEX_SHADER));
      gl.attachShader(p, compiler(gl, FS, gl.FRAGMENT_SHADER));
      gl.linkProgram(p);
      if (!gl.getProgramParameter(p, gl.LINK_STATUS))
        throw new Error("liaison : " + gl.getProgramInfoLog(p));
      this.prog = p;
      this.u = {};
      ["uMVP","uNrmMat","uLo","uEch","uTeinte","uMontrerZones","uDz","uStade",
       "uGrainReg","uNus","uGrainOn","uTexte","uPanneau","uLettre","uProfondeur",
       "uCreuse","uBiseau"].forEach(n => { this.u[n] = gl.getUniformLocation(p, n); });
      this.tex = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, this.tex);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.enable(gl.DEPTH_TEST);
      /* Pas d'élimination des faces arrière : le maillage est une coque ouverte,
         et l'éliminer laisserait voir le fond de la scène si un bord de coupe
         affleurait. Le shader retourne la normale des faces vues de dos. */
      gl.disable(gl.CULL_FACE);
    }

    /* Charge les deux pièces d'un format. Tant qu'elles n'arrivent pas, rien
       n'est dessiné et le canvas garde ce que les tuiles y avaient mis. */
    /* Charge les deux pièces d'un format, une fois et une seule.

       La mémoïsation des requêtes en cours n'est pas un raffinement : la
       méthode paint() est appelée plusieurs fois au montage — une fois par le composant, puis à
       chaque fonte qui finit de charger — et sans elle chaque appel relançait le
       téléchargement d'un maillage pas encore arrivé. Mesuré sur la fiche
       produit : le couvercle était récupéré trois fois, cent dix kilo-octets
       pour rien. */
    charger(format) {
      const urls = this.opts.paquets || {};
      const cles = [format + "-body", format + "-lid"];
      if (!this.enCours) this.enCours = {};
      const attentes = cles.map(cle => {
        if (this.pieces[cle]) return Promise.resolve();
        if (this.enCours[cle]) return this.enCours[cle];
        if (!urls[cle]) return Promise.resolve();
        const p = fetch(urls[cle]).then(r => {
          if (!r.ok) throw new Error(r.status + " sur " + cle);
          return r.arrayBuffer();
        }).then(buf => { this.pieces[cle] = this._televerser(cle, lirePaquet(buf)); })
          .then(() => { delete this.enCours[cle]; },
                (e) => { delete this.enCours[cle]; throw e; });
        this.enCours[cle] = p;
        return p;
      });
      return Promise.all(attentes).then(() => cles.every(c => this.pieces[c]));
    }

    _televerser(cle, m) {
      const gl = this.gl;
      const vao = gl.createVertexArray();
      gl.bindVertexArray(vao);
      const bp = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, bp);
      gl.bufferData(gl.ARRAY_BUFFER, m.pos, gl.STATIC_DRAW);
      gl.enableVertexAttribArray(0);
      gl.vertexAttribPointer(0, 3, gl.UNSIGNED_SHORT, true, 0, 0);
      const bn = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, bn);
      gl.bufferData(gl.ARRAY_BUFFER, m.nrm, gl.STATIC_DRAW);
      gl.enableVertexAttribArray(1);
      gl.vertexAttribPointer(1, 3, gl.BYTE, true, 0, 0);
      const bg = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, bg);
      gl.bufferData(gl.ARRAY_BUFFER, m.grain, gl.STATIC_DRAW);
      gl.enableVertexAttribArray(2);
      gl.vertexAttribPointer(2, 1, gl.UNSIGNED_BYTE, true, 0, 0);
      const bi = gl.createBuffer();
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, bi);
      gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, m.idx, gl.STATIC_DRAW);
      gl.bindVertexArray(null);
      const info = (this.opts.manifeste || {})[cle] || {};
      return { vao, m, info,
               type: m.idx.BYTES_PER_ELEMENT === 2 ? gl.UNSIGNED_SHORT : gl.UNSIGNED_INT };
    }

    /* Le texte est dessiné dans un canvas avec la fonte réelle, puis téléversé.
       Le shader n'en lit que la couverture : changer la couleur des lettres ne
       coûte donc pas un nouveau dessin. Le calcul d'ajustement est celui du
       configurateur, pour que le texte se place exactement comme sur les
       tuiles. */
    _texte() {
      const gl = this.gl;
      const pc = this.pieces[this.etat.format + "-lid"];
      const pan = (pc && pc.info.panneau) || [82.7, 18.5];
      const h = Math.round(PANNEAU_PX * pan[1] / pan[0]);
      const cv = document.createElement("canvas");
      cv.width = PANNEAU_PX; cv.height = h;
      const ctx = cv.getContext("2d");
      const t = String(this.etat.texte || "").trim();
      if (t) {
        const f = (this.opts.polices || [])[this.etat.police] || { famille: "sans-serif", graisse: 700, track: 0 };
        const REF = 200;
        ctx.textAlign = "center";
        ctx.textBaseline = "alphabetic";
        if ("letterSpacing" in ctx) ctx.letterSpacing = (f.track * REF / 40) + "px";
        ctx.font = f.graisse + " " + REF + "px " + f.famille;
        const m = ctx.measureText(t);
        const a0 = m.actualBoundingBoxAscent || REF * 0.72;
        const d0 = m.actualBoundingBoxDescent || REF * 0.2;
        const taille = Math.max(8, Math.min(PANNEAU_PX * 0.99 / m.width,
                                            h * 0.94 / (a0 + d0)) * REF);
        if ("letterSpacing" in ctx) ctx.letterSpacing = (f.track * taille / 40) + "px";
        ctx.font = f.graisse + " " + taille + "px " + f.famille;
        const mm = ctx.measureText(t);
        const asc = mm.actualBoundingBoxAscent || taille * 0.72;
        const desc = mm.actualBoundingBoxDescent || taille * 0.2;
        ctx.fillStyle = "#ffffff";
        ctx.fillText(t, PANNEAU_PX / 2, h / 2 + (asc - desc) / 2);
      }
      gl.bindTexture(gl.TEXTURE_2D, this.tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, cv);
      gl.generateMipmap(gl.TEXTURE_2D);
      this.texteVide = !t;
    }

    poser(etat) {
      const avant = this.etat.texte + "|" + this.etat.police + "|" + this.etat.format;
      Object.assign(this.etat, etat || {});
      const apres = this.etat.texte + "|" + this.etat.police + "|" + this.etat.format;
      return this.charger(this.etat.format).then(pret => {
        if (!pret) return false;
        if (apres !== avant || this.texteVide === undefined) this._texte();
        this.dessiner();
        return true;
      });
    }

    _boite() {
      const a = this.pieces[this.etat.format + "-body"], b = this.pieces[this.etat.format + "-lid"];
      const lo = [0,1,2].map(k => Math.min(a.m.lo[k], b.m.lo[k]));
      const hi = [0,1,2].map(k => Math.max(a.m.lo[k] + a.m.ech[k], b.m.lo[k] + b.m.ech[k]));
      const c = [0,1,2].map(k => (lo[k] + hi[k]) / 2);
      return { cible: c, R: Math.hypot(hi[0]-c[0], hi[1]-c[1], hi[2]-c[2]) };
    }

    /* Distance de prise de vue : le rayon de la sphère englobante, et rien
       d'autre qui dépende de l'orientation.

       C'est un choix, et il a une histoire. J'avais d'abord ajusté la distance à
       chaque image en projetant les huit coins de la boîte englobante, pour
       serrer le cadre au mieux de chaque vue. Le cadrage était plus rempli — de
       soixante-quatorze à quatre-vingt-dix pour cent, au lieu de soixante et un
       — mais l'étendue projetée de l'étui varie du simple au double selon qu'on
       le regarde de profil, trente-sept millimètres de large, ou du dessus,
       quatre-vingt-onze de long. La caméra reculait donc et avançait pendant
       qu'on tournait la pièce, et ce zoom continu est insupportable.

       La sphère englobante, elle, ne tourne pas avec l'objet : un cadrage
       calculé sur son rayon est immobile par construction, et ne déborde jamais
       dans aucune orientation. Le prix est de l'air autour de la pièce dans les
       vues étroites, et c'est au visiteur de le rattraper s'il le souhaite —
       d'où la molette et le pincement.

       Le min() sur les deux tangentes prend la dimension contraignante du cadre :
       sans lui, un aperçu plus haut que large — un téléphone étroit — laisserait
       l'étui sortir sur les côtés. */
    _distance(R, aspect) {
      const tanY = Math.tan(FOV / 2), tanX = tanY * aspect;
      return this.recul * R * SPHERE / Math.min(tanY, tanX);
    }

    /* Le zoom, borné. Ne redessine que si la valeur a bougé : aux deux butées,
       continuer à tourner la molette ne coûte plus rien. */
    _zoomer(r) {
      const v = Math.max(RECUL_MIN, Math.min(RECUL_MAX, r));
      if (v === this.recul) return;
      this.recul = v;
      this.dessiner();
    }

    dessiner() {
      const gl = this.gl, cv = this.canvas;
      const corps = this.pieces[this.etat.format + "-body"];
      const couv = this.pieces[this.etat.format + "-lid"];
      if (!corps || !couv) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, Math.round(cv.clientWidth * dpr));
      const h = Math.max(1, Math.round(cv.clientHeight * dpr));
      if (cv.width !== w || cv.height !== h) { cv.width = w; cv.height = h; }
      gl.viewport(0, 0, w, h);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

      const { cible, R } = this._boite();
      const dist = this._distance(R, w / h);
      const V = vue(this.azimut, this.elevation, dist, cible);
      const P = perspective(FOV, w / h, Math.max(1, dist - R * 2), dist + R * 3);
      gl.useProgram(this.prog);
      gl.uniformMatrix4fv(this.u.uMVP, false, new Float32Array(mul(P, V)));
      gl.uniformMatrix3fv(this.u.uNrmMat, false, new Float32Array(normaleDe(V)));
      gl.uniform1f(this.u.uMontrerZones, 0);
      gl.uniform1f(this.u.uGrainOn, 1);
      gl.uniform1f(this.u.uBiseau, GRAVURE_BISEAU);
      const creuse = String(this.etat.lettres).toLowerCase() === String(this.etat.couvercle).toLowerCase();
      gl.uniform1f(this.u.uCreuse, creuse ? 1 : 0);
      gl.uniform1f(this.u.uProfondeur, creuse ? GRAVURE_PROFONDEUR : 0);
      const cl = teinte(this.etat.lettres);
      gl.uniform3f(this.u.uLettre, cl[0], cl[1], cl[2]);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this.tex);
      gl.uniform1i(this.u.uTexte, 0);

      [["body", this.etat.corps], ["lid", this.etat.couvercle]].forEach(([suf, hex]) => {
        const pc = this.pieces[this.etat.format + "-" + suf];
        if (!pc) return;
        const m = pc.m, inf = pc.info;
        gl.bindVertexArray(pc.vao);
        gl.uniform3f(this.u.uLo, m.lo[0], m.lo[1], m.lo[2]);
        gl.uniform3f(this.u.uEch, m.ech[0], m.ech[1], m.ech[2]);
        gl.uniform1f(this.u.uDz, 0);
        const c = teinte(hex);
        gl.uniform3f(this.u.uTeinte, c[0], c[1], c[2]);
        /* Le nombre de cellules sur le tour doit être entier, sinon le bruit ne
           se referme pas et une couture apparaît là où le contour boucle. */
        const per = 4 * (inf.L || 27) + 2 * Math.PI * (inf.R || 18.5);
        const nu = Math.max(8, Math.round(per / GRAIN_TAILLE));
        gl.uniform2f(this.u.uStade, inf.R || 18.5, inf.L || 27);
        gl.uniform2f(this.u.uNus, nu, Math.max(8, Math.round(nu * 2.3)));
        gl.uniform4f(this.u.uGrainReg, GRAIN_TAILLE, GRAIN_TAILLE / 2, GRAIN_RELIEF, GRAIN_CREUX);
        const grave = suf === "lid" && !this.texteVide;
        gl.uniform4f(this.u.uPanneau, (inf.panneau || [82.7])[0], (inf.panneau || [0, 18.5])[1],
                     inf.zDessus || 0, grave ? 1 : 0);
        gl.drawElements(gl.TRIANGLES, m.nf * 3, pc.type, 0);
      });
      gl.bindVertexArray(null);
    }

    /* L'orbite et le zoom. Le sens du glissé est celui des tuiles — vers la
       droite fait tourner la pièce vers la droite — pour ne pas dérouter qui
       connaît déjà la boutique.

       Un seul modèle d'événements pour les deux gestes : les pointeurs. Mêler
       les événements tactiles aux pointeurs pour obtenir le pincement aurait
       doublé le nombre de sources à tenir d'accord, alors qu'un registre des
       doigts posés suffit — un doigt tourne, deux doigts zooment. Le tactile
       natif est de toute façon hors jeu, touch-action valant none pour que le
       glissé vertical commande l'élévation. */
    _brancher() {
      const cv = this.canvas;
      const doigts = new Map();   // pointerId -> position, dans l'ordre de pose
      let prise = null;           // l'orbite en cours, s'il y en a une
      let pince = null;           // l'écartement au début du pincement

      const ecart = () => {
        const p = Array.from(doigts.values());
        return Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y);
      };
      const orbiter = (id) => {
        const p = doigts.get(id);
        prise = { id, x: p.x, y: p.y, az: this.azimut, el: this.elevation };
      };

      /* Un doigt fantôme bloquait tout en zoom (signalé le 3 octobre 2026 sur
         téléphone, non reproductible ensuite). Si le navigateur ne livre jamais
         le lever d'un doigt — geste du système, changement d'application, perte
         de la capture —, ce doigt reste dans le registre ; chaque nouveau doigt
         y devient alors le deuxième, et tout geste se lit comme un pincement
         par rapport au fantôme : la pièce ne fait plus qu'avancer et reculer,
         jusqu'au rechargement de la page.

         Trois verrous, du plus sûr au plus large. Un pointeur « principal » est
         le premier d'une séquence, posé quand aucun autre ne touche l'écran :
         s'il reste quelqu'un dans le registre à ce moment-là, c'est un fantôme,
         on vide. La perte de capture vaut un lever. Et la page qui passe en
         arrière-plan vide tout. */
      const vider = () => { doigts.clear(); prise = null; pince = null; };

      this._surPointerDown = (e) => {
        if (e.isPrimary && doigts.size) vider();
        doigts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        try { cv.setPointerCapture(e.pointerId); } catch (err) {}
        if (doigts.size >= 2) {
          /* Le deuxième doigt interrompt l'orbite : sans cela la pièce tournerait
             pendant le pincement, au gré de celui des deux doigts qui bouge. */
          prise = null;
          pince = { d: ecart(), recul: this.recul };
        } else {
          orbiter(e.pointerId);
        }
      };

      this._surPointerMove = (e) => {
        if (!doigts.has(e.pointerId)) return;
        doigts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pince && doigts.size === 2) {
          const d = ecart();
          // écarter les doigts rapproche la pièce, d'où le rapport inversé
          if (pince.d > 8 && d > 8) this._zoomer(pince.recul * pince.d / d);
          return;
        }
        if (!prise || e.pointerId !== prise.id) return;
        this.azimut = prise.az - (e.clientX - prise.x) * TOURNE;
        this.elevation = Math.max(ELEV_MIN, Math.min(ELEV_MAX,
          prise.el + (e.clientY - prise.y) * TOURNE * 0.75));
        this.dessiner();
      };

      this._surPointerUp = (e) => {
        doigts.delete(e.pointerId);
        if (doigts.size < 2) pince = null;
        /* Un doigt levé après un pincement : celui qui reste doit reprendre
           l'orbite depuis sa position actuelle, sinon la pièce sauterait de tout
           l'écart accumulé pendant le pincement. */
        if (doigts.size === 1) orbiter(Array.from(doigts.keys())[0]);
        else if (!doigts.size) prise = null;
      };

      /* La molette seule ne zoome pas, et c'est un choix corrigé après usage.
         Le banc d'essai zoomait au défilement, ce qui y était commode — la page
         ne servait qu'à ça. Sur une fiche produit, le même geste retient le
         défilement dès que le pointeur passe sur l'étui : on croit lire la page,
         elle se bloque. C'est le défaut des cartes intégrées, et il a été
         reproché à la première version.

         Ne reste donc que le pincement du pavé tactile, qui arrive par ce même
         événement : macOS et Windows l'envoient avec ctrlKey, comme le zoom du
         navigateur. Celui-là exprime sans ambiguïté une intention de zoom, et le
         retenir est même nécessaire — sinon c'est la page entière qui grossit.

         Le pas suit l'amplitude au lieu de son signe : un cran de molette vaut
         cent, un pincement de pavé tactile quelques unités, et le même pas fixe
         rendrait le second dix fois trop rapide. Il est borné à un cran pour
         qu'un cran reste un cran. */
      this._surMolette = (e) => {
        if (!e.ctrlKey) return;
        if (e.cancelable) e.preventDefault();
        const k = Math.max(-1, Math.min(1, e.deltaY / 50));
        this._zoomer(this.recul * Math.exp(k * PAS_ZOOM));
      };

      // Le double-clic remet le cadrage d'origine : sans lui, un zoom poussé à
      // la butée est une impasse.
      this._surDouble = () => this._zoomer(1);

      this._surResize = () => this.dessiner();
      cv.addEventListener("pointerdown", this._surPointerDown);
      cv.addEventListener("pointermove", this._surPointerMove);
      cv.addEventListener("pointerup", this._surPointerUp);
      cv.addEventListener("pointercancel", this._surPointerUp);
      cv.addEventListener("lostpointercapture", this._surPointerUp);
      this._surCache = () => { if (document.hidden) vider(); };
      document.addEventListener("visibilitychange", this._surCache);
      window.addEventListener("blur", vider);
      this._vider = vider;
      cv.addEventListener("wheel", this._surMolette, { passive: false });
      cv.addEventListener("dblclick", this._surDouble);
      window.addEventListener("resize", this._surResize);
      cv.style.touchAction = "none";
      cv.style.cursor = "grab";
    }

    detruire() {
      const cv = this.canvas;
      cv.removeEventListener("pointerdown", this._surPointerDown);
      cv.removeEventListener("pointermove", this._surPointerMove);
      cv.removeEventListener("pointerup", this._surPointerUp);
      cv.removeEventListener("pointercancel", this._surPointerUp);
      cv.removeEventListener("lostpointercapture", this._surPointerUp);
      document.removeEventListener("visibilitychange", this._surCache);
      window.removeEventListener("blur", this._vider);
      cv.removeEventListener("wheel", this._surMolette);
      cv.removeEventListener("dblclick", this._surDouble);
      window.removeEventListener("resize", this._surResize);
    }
  }

  function teinte(hex) {
    const s = String(hex || "#808080");
    return [1, 3, 5].map(i => parseInt(s.slice(i, i + 2), 16) / 255)
      .map(v => (isFinite(v) ? v : 0.5));
  }

  window.MwfScene3D = {
    /* Un seul point d'entrée, et il ne lève jamais : si quoi que ce soit
       manque, il renvoie null et le configurateur garde ses tuiles. */
    creer: function (canvas, opts) {
      try {
        if (!canvas || !window.WebGL2RenderingContext) return null;
        if (!opts || !opts.paquets) return null;
        return new Scene3D(canvas, opts);
      } catch (e) {
        if (window.console) console.warn("[mwf-3d] repli sur les tuiles :", e && e.message);
        return null;
      }
    }
  };
})();
